import {
  AlertTitle,
  Card,
  CardContent,
  Tooltip,
  toast,
  Button,
  Badge,
  ButtonProps,
  Typography,
} from "@altimateai/lego";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { CopyIconButton } from "../../components/copyButton";
import { IconChevronLeft, IconExclamationCircle } from "@tabler/icons-react";
import { Lineage, SqlLineage } from "../../components/lineage";
import type { LineageProps } from "../../components/lineage";
import type { StaticLineageDetails } from "@altimateai/ui-components/lineage";
import { Tab } from "../../components/tab";
import { useQuery } from "@tanstack/react-query";
import { ComponentLoader, RelativeComponentLoader } from "../../components/loader";
import { isFreeStack, isLineage } from "../../helpers/tenant";
import { Schema } from "./Schema";
import {
  downstreamTablesDBT,
  getSQLLineage,
  postColumnsDBT,
  postConnectedColumnsDBT,
  startingNodeDBT,
  upstreamTablesDBT,
} from "../../helpers/apis";
import { GET_DBT_MODEL_BY_ID, GET_SQL_LINEAGE_BY_ID } from "./constants";
import { CodeDiffEditor, CodeSQLEditor } from "@/components/codeEditor";
import { useSession } from "../../helpers/hooks";
import { NoCompiledSql, NoCompiledSqlTest } from "./NoDbtModels";
import { Stack } from "@altimateai/lego";
import { Alert, AlertDescription } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import dayjs from "dayjs";
import { DbtIcon } from "@assets/icons/index";
import { useDateFromSearchParams, useResourceOpportunities } from "@/helpers/useTimeHooks";
import { ResourceType, GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import { InsightSection } from "@/pages/query/components";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { DbtRuns } from "@/pages/dbtModels/DbtRuns";
import React, { useEffect, useRef, useState } from "react";
import { DbtModel } from "@/types/dbtModels";
import { ViewsCodeModalArgs } from "@altimateai/ui-components/lineage";
import { fetchDbtInfo, getDbtModelByRk } from "@/helpers/apis";
import { Drawer } from "@/uiCore";
import { ViewsCodeSideModal } from "../datasets/components/ViewCodeSideModal";
import { DrawerRef } from "@/uiCore/components/drawer";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { DbtModelsTracking } from "@/lib/tracking/dbtModels";

const MainContent = ({
  dbtModel,
  opportunities,
  isOpportunitiesLoading,
}: {
  dbtModel: DbtModel;
  opportunities: Array<GetOpportunitiesDataResponse> | undefined;
  isOpportunitiesLoading: boolean;
}) => {
  const { staleResourcesEnabled, enableOpportunityTicketCreation } = useAppState();
  const isFree = isFreeStack();
  let { dbtModelId } = useParams();
  dbtModelId = dbtModelId ? atob(dbtModelId) : "";
  const navigate = useNavigate();
  const lineage = isLineage();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const selectedOpportunityId = searchParams.get("opportunityId");
  const setSelectedOpportunityId = (v: string) => {
    updateSearchParamsByKey({ opportunityId: v });
    if (v) {
      DbtModelsTracking.opportunitySelected(dbtModel.model_name, v);
    }
  };
  const { startDate, endDate, onUpdate: onDateRangeChange } = useDateFromSearchParams();
  const [viewCodeModalArgs, setViewCodeModalArgs] = useState<
    (ViewsCodeModalArgs & { label: string }) | null
  >(null);
  const drawerRef = useRef<DrawerRef | null>(null);

  useEffect(() => {
    if (viewCodeModalArgs) {
      drawerRef.current?.open();
    } else {
      drawerRef.current?.close();
    }
  }, [viewCodeModalArgs]);

  const tabs: {
    label: string;
    component: React.ReactNode;
    disabled?: boolean;
    betaMode?: boolean;
  }[] = [
    {
      label: "Schema",
      component: <Schema table={dbtModel} />,
    },
    {
      label: "Lineage",
      component: (
        <>
          {viewCodeModalArgs && (
            <Drawer ref={drawerRef} style={{ width: "600px" }}>
              <ViewsCodeSideModal viewsCodeArgs={viewCodeModalArgs} disableQueryTextView />
            </Drawer>
          )}
          <Lineage
            tableId={dbtModelId}
            getStartingNode={startingNodeDBT as LineageProps["getStartingNode"]}
            postColumns={postColumnsDBT as LineageProps["postColumns"]}
            upstreamTables={upstreamTablesDBT as LineageProps["upstreamTables"]}
            downstreamTables={downstreamTablesDBT as LineageProps["downstreamTables"]}
            postConnectedColumns={postConnectedColumnsDBT as LineageProps["postConnectedColumns"]}
            dbtInfo={fetchDbtInfo}
            setViewCodeModalArgs={args =>
              setViewCodeModalArgs(args as (ViewsCodeModalArgs & { label: string }) | null)
            }
            showViewCodeModal={!!viewCodeModalArgs}
            exportFinalLineage={false}
          />
        </>
      ),
      disabled: !lineage,
    },
  ];

  const resourceType = dbtModel?.resource_type;
  if (["model", "test"].includes(resourceType)) {
    tabs.push({
      label: "Code",
      component: <DbtCode rawCode={dbtModel.raw_code} compiledCode={dbtModel.compiled_code} />,
    });
    tabs.push({
      label: "SQL Visualizer",
      betaMode: true,
      component: (
        <SQLLineage
          integrationId={dbtModel.dbt_core_integration_id}
          integrationEnvironmentId={dbtModel.dbt_core_integration_environment_id}
          resourceType={resourceType}
          dbtModelId={dbtModelId}
          compiledCode={dbtModel?.compiled_code}
          modelName={dbtModel.model_name}
        />
      ),
    });
    if (!isFree) {
      tabs.push({
        label: "Runs",
        component: (
          <DatatableContextProvider>
            <DbtRuns model={dbtModel} />
          </DatatableContextProvider>
        ),
      });
    }
  }

  if (["model", "source", "test"].includes(resourceType)) {
    tabs.push({
      label: "Opportunities",
      component: isOpportunitiesLoading ? (
        <RelativeComponentLoader label={"Loading opportunities"} />
      ) : (
        <InsightSection
          opportunities={opportunities ?? []}
          selectedOpportunityId={selectedOpportunityId}
          setOpportunityId={setSelectedOpportunityId}
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={onDateRangeChange}
          showActionsDropdown={enableOpportunityTicketCreation}
          integrationIds={["altimate", "power-user-for-dbt", "dbt"]}
          totalRuns={""}
        />
      ),
    });
  }

  const handleTabChange = (label: string) => {
    const modelName = dbtModel.model_name;
    switch (label) {
      case "Schema":
        DbtModelsTracking.schemaViewed(modelName);
        break;
      case "Lineage":
        DbtModelsTracking.lineageViewed(modelName);
        break;
      case "Code":
        DbtModelsTracking.codeViewed(modelName);
        break;
      case "SQL Visualizer":
        DbtModelsTracking.sqlVisualizerViewed(modelName);
        break;
      case "Runs":
        DbtModelsTracking.runsViewed(modelName);
        break;
    }
  };

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  return (
    <Stack direction="column">
      <Stack gap="sm" className="mb-3">
        <Button
          role={"link"}
          variant={"ghost"}
          onClick={handleBackButtonClick}
          className={"al-pt-0 hover:al-bg-transparent"}
        >
          <IconChevronLeft className={"!al-size-5"} />
        </Button>
        <Stack direction="column" gap="sm" className="ms-2 me-2 al-w-full">
          <Stack align="center">
            <DbtIcon className={"al-h-5 al-w-5 al-text-brand me-2"} />
            <div className="fs-4 text-primary me-2">{dbtModel.model_name}</div>
            <CopyIconButton value={dbtModel.model_name} colorClassName="al-text-primary" />
            {staleResourcesEnabled && dbtModel.is_stale && dbtModel.last_seen && (
              <Tooltip
                title={`Last seen on ${dayjs(dbtModel.last_seen).format("DD MMM YYYY, hh:mm A")}`}
              >
                <Badge
                  className="al-font-semibold al-bg-yellow-100 al-text-yellow-800 al-border-yellow-200 ms-2"
                  variant="warning"
                >
                  Stale
                </Badge>
              </Tooltip>
            )}
          </Stack>
          <Stack wrap gap="2xl">
            <Stack>
              <Typography size="sm" className="al-text-gray">
                Project Name:{" "}
              </Typography>
              <Typography size="sm">{dbtModel.project_name}</Typography>
            </Stack>
            {/* Stack>
              <Typography size="sm" className="al-text-gray">Package Name: </Typography>
              <Typography>{dbtModel.package_name}</Typography>
            </Stack> */}
            <Stack>
              <Typography size="sm" className="al-text-gray">
                Path:{" "}
              </Typography>
              <Typography size="sm">{dbtModel.file_path}</Typography>
            </Stack>
          </Stack>
          <Stack wrap gap="2xl">
            <Stack>
              <Typography size="sm" className="al-text-gray">
                Resource Type:{" "}
              </Typography>
              <Typography size="sm">{dbtModel.resource_type}</Typography>
            </Stack>
            {resourceType === "model" && dbtModel.materialization && (
              <Stack>
                <Typography size="sm" className="al-text-gray">
                  Materialization:{" "}
                </Typography>
                <Typography size="sm">{dbtModel.materialization}</Typography>
              </Stack>
            )}
          </Stack>
          <Stack wrap gap="2xl">
            <Stack>
              <Typography size="sm" className="al-text-gray">
                Database:{" "}
              </Typography>
              <Typography size="sm">{dbtModel.database}</Typography>
            </Stack>
            <Stack>
              <Typography size="sm" className="al-text-gray">
                Schema:{" "}
              </Typography>
              <Typography size="sm">{dbtModel.model_schema}</Typography>
            </Stack>
            {dbtModel.alias && (
              <Stack>
                <Typography size="sm" className="al-text-gray">
                  Alias:{" "}
                </Typography>
                <Typography size="sm">{dbtModel.alias}</Typography>
              </Stack>
            )}
          </Stack>
          <Stack gap="md" className="flex-wrap" align="center">
            {dbtModel.insights && dbtModel.insights.length > 0 && (
              <>
                {dbtModel.insights.map(({ name, severity }, index: number) => {
                  let variant: ButtonProps["variant"];
                  switch (severity) {
                    case "severity.WARNING":
                      variant = "outline";
                      break;
                    case "severity.ERROR":
                      variant = "destructive";
                      break;
                    default:
                      variant = "default";
                  }
                  return (
                    <Badge key={index} variant={variant} className="al-text-xs">
                      {name}
                    </Badge>
                  );
                })}
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
      {staleResourcesEnabled && dbtModel.is_stale && dbtModel.last_seen && (
        <Alert variant={"warning"} className="al-mb-4">
          <IconExclamationCircle className={"al-size-5"} />
          <AlertTitle className={"al-text-base"}>Stale Resource</AlertTitle>
          <AlertDescription>
            This resource was last seen on{" "}
            {dayjs(dbtModel.last_seen).format("DD MMM YYYY, hh:mm A")}
          </AlertDescription>
        </Alert>
      )}
      <Card className="al-h-full">
        <CardContent className="al-pt-4 al-h-full">
          <Tab tabs={tabs} fullscreen={true} onSelectedTabChange={handleTabChange} />
        </CardContent>
      </Card>
    </Stack>
  );
};

const DbtCode = ({
  rawCode,
  compiledCode,
}: {
  rawCode?: string | null;
  compiledCode?: string | null;
}) => {
  return (
    <CodeDiffEditor
      height="68vh"
      editor1Value={rawCode ?? undefined}
      editor2Value={compiledCode ?? undefined}
      title1="Raw Code"
      title2="Compiled Code"
      language="sql"
      readOnly
      lineNumbers
      minimap
    />
  );
};

interface SQLLineageProps {
  dbtModelId: string;
  compiledCode?: string | null;
  modelName: string;
  integrationId?: number;
  integrationEnvironmentId?: number;
  resourceType: string;
}

function isStaticLineageDetails(details: Record<string, unknown>): details is StaticLineageDetails {
  return Object.values(details).every(
    node =>
      typeof node === "object" &&
      node !== null &&
      typeof (node as Record<string, unknown>).type === "string"
  );
}

const SQLLineage = ({
  dbtModelId,
  compiledCode,
  modelName,
  integrationId,
  integrationEnvironmentId,
  resourceType,
}: SQLLineageProps) => {
  const session_id = useSession();
  const { data, isLoading } = useQuery({
    queryKey: [GET_SQL_LINEAGE_BY_ID, dbtModelId],
    queryFn: async () => {
      const result = await getSQLLineage({
        dbt_model_rk: dbtModelId,
        session_id,
      });
      if (!result) return undefined;
      let { tableEdges, details, nodePositions } = result;
      const FINAL_SELECT = "__final_select__";
      const replaceFinalSelect = (t: string) => (t === FINAL_SELECT ? modelName : t);
      tableEdges = tableEdges.map((e: string[]) => e.map(replaceFinalSelect)) as [string, string][];
      details[modelName] = details[FINAL_SELECT];
      delete details[FINAL_SELECT];
      if (nodePositions) {
        nodePositions[modelName] = nodePositions[FINAL_SELECT];
        delete nodePositions[FINAL_SELECT];
      }
      return {
        tableEdges,
        details: isStaticLineageDetails(details) ? details : undefined,
        nodePositions: nodePositions ?? undefined,
      };
    },
  });

  if (!compiledCode && resourceType === "test") {
    return (
      <NoCompiledSqlTest
        modelName={modelName}
        integrationId={integrationId}
        integrationEnvironmentId={integrationEnvironmentId}
      />
    );
  }

  if (!compiledCode) {
    return (
      <NoCompiledSql
        modelName={modelName}
        integrationId={integrationId}
        integrationEnvironmentId={integrationEnvironmentId}
      />
    );
  }

  if (isLoading) return <RelativeComponentLoader label={"Loading..."} />;
  return (
    <div className="al-grid al-grid-cols-1 md:al-grid-cols-2 al-gap-4">
      <div>
        <SqlLineage {...data} height="76vh" />
      </div>
      <div>
        <CodeSQLEditor value={compiledCode} height="76vh" lineNumbers />
      </div>
    </div>
  );
};

const DbtModelsDetails = () => {
  let { dbtModelId } = useParams();
  dbtModelId = dbtModelId ? atob(dbtModelId) : "";

  const opportunities = useResourceOpportunities({
    resourceUri: dbtModelId,
    resourceType: ResourceType.DbtModel,
  });
  const {
    data: dbtModelData,
    isLoading: isDbtModelLoading,
    error: dbtModelError,
  } = useQuery({
    queryKey: [GET_DBT_MODEL_BY_ID, dbtModelId],
    queryFn: () => getDbtModelByRk({ dbt_model_rk: dbtModelId! }),
    retry: false,
    enabled: !!dbtModelId,
  });

  useEffect(() => {
    if (dbtModelError) {
      toast({
        description: (dbtModelError as Error)?.message || String(dbtModelError),
        variant: "destructive",
      });
    }
  }, [dbtModelError]);

  useBreadcrumb(dbtModelData?.model_name ?? "Loading...", {
    defaultParents: [{ label: "dbt Models", pathname: "/dbt_models" }],
  });

  useEffect(() => {
    if (dbtModelData) {
      DbtModelsTracking.detailViewed(dbtModelData.model_name, dbtModelData.resource_type);
    }
  }, [dbtModelData]);

  if (isDbtModelLoading) {
    return <ComponentLoader label={"Loading"} />;
  }
  if (!dbtModelData) return <Navigate to="/dbt_models" />;

  const dbtModel = dbtModelData as unknown as DbtModel;

  return (
    <MainContent
      dbtModel={dbtModel}
      opportunities={opportunities?.data?.data}
      isOpportunitiesLoading={opportunities.isFetching}
    />
  );
};

export { DbtModelsDetails };
